import * as _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import type { RefObject } from 'react';

type IntersectionObserverOptions = {
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/IntersectionObserver/root) */
  root?: Element | Document | null;
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/IntersectionObserver/rootMargin) */
  rootMargin?: string;
  /** [MDN Reference](https://developer.mozilla.org/docs/Web/API/IntersectionObserver/thresholds) */
  threshold?: number | number[];
};

/**
 * Hook for determining whether an element is visible on the screen.
 * Uses the IntersectionObserver API to determine whether the element is
 * intersecting with another element or the view point, indicating that the element
 * is visible.
 *
 * @param {RefObject} ref - React ref to the element to observe
 * @param {IntersectionObserverOptions} options - IntersectionObserver options
 * @returns {boolean} - Whether the element is visible on the screen
 *
 * @example
 * import React from 'react';
 * import { useIsVisibleOnScreen } from '../hooks';
 *
 * const MyComponent = () => {
 *  const ref = useRef(null);
 *  const isVisible = useIsVisibleOnScreen(ref);
 *
 *   return (
 *     <div ref={ref}>
 *      { isVisible.toString() }
 *     </div>
 *   );
 * }
 */
export default function useIsVisibleOnScreen(ref: RefObject<HTMLElement>, options?: IntersectionObserverOptions) {
  const [isIntersecting, setIntersecting] = useState(false);

  const observer = useMemo(() => new IntersectionObserver(
    ([entry]) => setIntersecting(entry?.isIntersecting || false),
    options,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [ref]);

  useEffect(() => {
    if (!ref.current) {
      return _.noop;
    }

    observer.observe(ref.current);
    return () => observer.disconnect();
  });

  return isIntersecting;
}
